import { FormattedMessage, useIntl } from 'react-intl';
import { preparePlaceholders } from '../../utils/localizedTagsUtils';

export interface LocalizedTextProps {
  /**
   * The localized text, or the translation token
   */
  text: string;
  /**
   * If true, the text is localized, otherwise it's a translation token
   */
  isLocalized: boolean;
  /**
   * A map of placeholder keys to their values. These placeholders can be used in the translation token for dynamic content.
   */
  placeholders?: {
    [k: string]: string;
  };
}

/**
 * LocalizedText is a React component that renders either a localized string using react-intl's FormattedMessage,
 * or a plain text string, depending on the `isLocalized` prop.
 *
 * It is used to render the text of the event tags in the event details page.
 *
 * - If `isLocalized` is true, it treats `text` as a translation key and uses `FormattedMessage` to render the localized string.
 *   - If `placeholders` are provided, they are passed as values to the translation.
 *   - Special handling is applied for `category_string` and `format_string` placeholders: if present and non-empty,
 *     their values are also localized using `formatMessage` before being passed to FormattedMessage.
 * - If `isLocalized` is false, it renders the `text` prop as plain text inside a <span>.
 *
 * @component
 * @param {Object} props
 * @param {LocalizedTextProps} props.text - The text configuration object, including the translation key, localization flag, and optional placeholders.
 * @returns {ReactElement} The rendered localized or plain text.
 */

export const LocalizedText: React.FC<{ text: LocalizedTextProps }> = ({ text }) => {
  const intl = useIntl();

  if (text.isLocalized) {
    const placeHolders = preparePlaceholders(text.placeholders, intl);
    return <FormattedMessage id={text.text} values={placeHolders} />;
  }
  return <span>{text.text}</span>;
};
